import { ENVS, PATH_AUTH, PATH_DASHBOARD } from "config";
import { ExportContextProvider } from "context/ExportContext";
import { ModalDialogCounterProvider } from "context/ModalDialogCounterContext";
import { ScriptContextProvider } from "context/ScriptProvider";
import { RedirectHandler } from "components/auth";
import { PosthogSupportIdentitySync } from "components/auth/PosthogSupportIdentitySync";
import { PosthogSupportWidgetGuard } from "components/auth/PosthogSupportWidgetGuard";
import React, { Suspense, useRef } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { buildLoginRedirectPath } from "utils/redirectUtils";
import RouteFallback from "routes/RouteFallback";
import { NetworkError } from "components/common/NetworkError/NetworkError";
import { useSessionUser } from "hooks/useSession";
import { sessionKeys } from "queries/profile.keys";
import type { AppState } from "store/reducers/Reducer";
import type { SessionUser } from "types/session-user";

export const PrivateLayout: React.FC = () => {
  const token = useSelector((state: AppState) => state.auth.token);
  const location = useLocation();
  const queryClient = useQueryClient();
  const cachedSession = queryClient.getQueryData<SessionUser>(sessionKeys.me());
  const {
    data: sessionUser,
    isInitialLoading: isSessionLoading,
    isError: isSessionError,
    refetch: refetchSession,
  } = useSessionUser();

  // Set once this layout has seen a signed-in user. A later bounce to login is a sign-out.
  const wasSignedInRef = useRef(false);
  if (token) {
    wasSignedInRef.current = true;
  }

  // Logged-out visitors go to login with the page they asked for, so they come back to it.
  // After a sign-out the page belongs to the account that left, and whoever signs in next
  // may be someone else, so login gets no return path.
  const loginPath = wasSignedInRef.current
    ? PATH_AUTH.login
    : buildLoginRedirectPath(PATH_AUTH.login, location);

  // Disabled session queries stay isLoading forever in RQ v4; redirect before waiting.
  if (!token && !cachedSession) {
    return <Navigate to={loginPath} replace />;
  }

  if (isSessionError) {
    return (
      <div className="min-h-screen bg-card flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <NetworkError
            title="Couldn't load your account"
            description="We couldn't verify your session. Check your connection, then try again."
            onRetry={() => {
              void refetchSession();
            }}
          />
        </div>
      </div>
    );
  }

  if (isSessionLoading) {
    return <RouteFallback containerClassName="min-h-screen" />;
  }

  if (!sessionUser) {
    return <Navigate to={loginPath} replace />;
  }

  if (!sessionUser.verified && !ENVS.onPrem) {
    return <Navigate to={PATH_AUTH.resendEmail} />;
  }

  if (!sessionUser.is_active) {
    return <Navigate to={PATH_DASHBOARD.activateAccount} />;
  }

  return (
    <main className="flex h-dvh min-h-svh flex-col overflow-hidden bg-canvas bg-cover text-foreground/80">
      <PosthogSupportIdentitySync />
      <PosthogSupportWidgetGuard />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <RedirectHandler>
            <ScriptContextProvider>
              <ExportContextProvider>
                <ModalDialogCounterProvider>
                  <Suspense fallback={<RouteFallback containerClassName="min-h-screen" />}>
                    <Outlet />
                  </Suspense>
                </ModalDialogCounterProvider>
              </ExportContextProvider>
            </ScriptContextProvider>
          </RedirectHandler>
        </div>
      </div>
    </main>
  );
};
